import { AdvancedMarker, useAdvancedMarkerRef } from '@vis.gl/react-google-maps'
import React, { useCallback, useMemo } from 'react'

import { ServiceMarker, ServiceMarkerHandler, ServiceMarkerLeaveHandler } from 'components/configured-service/Schedule/types'

// Faded, not hidden: a dimmed marker still reads as a stop that exists and stays clickable.
const DIMMED_STYLE = { opacity: 0.28 }

// Everything past the named props is the caller's marker record, handed back untouched so it can find the row.
type SharedMapMarkerProps = ServiceMarker & {
  children?: React.ReactNode
  onClick?: ServiceMarkerHandler
  onMouseEnter?: ServiceMarkerHandler
  onMouseLeave?: ServiceMarkerLeaveHandler
  zIndex?: number
  isDimmed?: boolean
}

const SharedMapMarker = ({
  id,
  latitude,
  longitude,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  zIndex = 1,
  isDimmed = false,
  ...otherDetails
}: SharedMapMarkerProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef()

  const mappedDetails: ServiceMarker = useMemo(() => {
    return {
      id,
      latitude,
      longitude,
      ...otherDetails,
    }
  }, [id, latitude, longitude, otherDetails])

  const handleMouseEnter = useCallback(
    (event: Event) => {
      if (onMouseEnter) onMouseEnter(event, marker, mappedDetails)
    },
    [onMouseEnter, marker, mappedDetails]
  )

  const handleMouseLeave = useCallback(
    (event: Event) => {
      if (onMouseLeave) onMouseLeave(event, marker, mappedDetails)
    },
    [onMouseLeave, marker, mappedDetails]
  )

  const handleMarkerClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (onClick) onClick(event, marker, mappedDetails)
    },
    [onClick, marker, mappedDetails]
  )

  return (
    <AdvancedMarker
      ref={markerRef}
      position={{ lat: latitude, lng: longitude }}
      zIndex={zIndex}
      onClick={handleMarkerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isDimmed ? <div style={DIMMED_STYLE}>{children}</div> : children}
    </AdvancedMarker>
  )
}

export default React.memo(SharedMapMarker)
